import { createSelector } from "reselect";

import settingsErrors from "../../constants/settingsErrors";
import { calendarMarkerSelectors } from "../../modules/calendarMarkers";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import dateFunctions from "../../modules/definition/calendar/dateFunctions";
import dateWithTimestamp from "../../modules/definition/calendar/dateFunctions/dateWithTimestamp";
import { getZeroDateTimestampFromCalendar } from "../../modules/definition/calendar/selectors";
import { colorSelectors } from "../../modules/definition/colors";
import { narrativeSettingsSelectors } from "../../modules/definition/narrativeSettings";
import { typeSelectors } from "../../modules/definition/types";
import { itemSelectors } from "../../modules/items";
import { Precision } from "../../types";

const getAllOrderedTypes = state => {
  return state.definitions.types.allIds.map(id => state.definitions.types.byId.get(id));
};

const getAllOrderedProperties = state => {
  return state.definitions.properties.allIds.map(id => state.definitions.properties.byId.get(id));
};

const getAllOrderedReferences = state => {
  return state.definitions.references.allIds.map(id => state.definitions.references.byId.get(id));
};

const eventTypesFromAllTypes = allTypes => allTypes.filter(typeSelectors.isEventType);
const eventTypesFromAllTypesExcludingNarrativeFolder = allTypes =>
  allTypes.filter(t => typeSelectors.isEventType(t) && !t.isNarrativeFolder);
const personTypesFromAllTypes = allTypes => allTypes.filter(typeSelectors.isPersonType);
const personTypesFromAllTypesExcludingNarrativeFolder = allTypes =>
  allTypes.filter(t => typeSelectors.isPersonType(t) && !t.isNarrativeFolder);
const locationTypesFromAllTypes = allTypes => allTypes.filter(typeSelectors.isLocationType);
const locationTypesFromAllTypesExcludingNarrativeFolder = allTypes =>
  allTypes.filter(t => typeSelectors.isLocationType(t) && !t.isNarrativeFolder);
const otherTypesFromAllTypes = allTypes => allTypes.filter(typeSelectors.isOtherType);
const otherTypesFromAllTypesExcludingNarrativeFolder = allTypes =>
  allTypes.filter(t => typeSelectors.isOtherType(t) && !t.isNarrativeFolder);

const getNarrativeFolderType = state => state.definitions.types.byId.find(t => t.isNarrativeFolder);

const getAllOrderedNarrativeTypes = state => getAllOrderedTypes(state).filter(typeSelectors.isNarrativeType);

const getFirstNarrativeTypeFromAllTypes = state => {
  const narrativeTypes = getAllOrderedNarrativeTypes(state);
  return narrativeTypes.size > 0 ? narrativeTypes.get(0) : null;
};

const eventToPersonReferences = (references, eventTypes, personTypes) =>
  references.filter(
    r => eventTypes.find(t => t.references.includes(r.id)) && r.types.find(tId => personTypes.find(t => t.id === tId))
  );
const eventToEventReferences = (references, eventTypes) =>
  references.filter(
    r => eventTypes.find(t => t.references.includes(r.id)) && r.types.find(tId => eventTypes.find(t => t.id === tId))
  );
const personToPersonReferences = (references, personTypes) =>
  references.filter(
    r => personTypes.find(t => t.references.includes(r.id)) && r.types.find(tId => personTypes.find(t => t.id === tId))
  );
const subjectTypeReferences = (references, types) =>
  references.filter(r => r.types.find(tId => types.find(t => t.id === tId)));

const propertiesForItemTypes = (properties, itemTypes) =>
  properties.filter(p => itemTypes.find(t => t.properties.includes(p.id)));

const commonPropertiesForItemTypes = (properties, eventTypes, personTypes, locationTypes) =>
  properties.filter(
    p =>
      eventTypes.find(t => t.properties.includes(p.id)) &&
      personTypes.find(t => t.properties.includes(p.id)) &&
      locationTypes.find(t => t.properties.includes(p.id))
  );

/**
 * At least one event type existed AND
 * if narrative allowed then at least one narrative type existed
 * Also: one narrative section label ticked for folder and one for other, if narrative is enabled
 */
const isSettingsValid = state => {
  const narrativeTypes = typeSelectors.getOrderedNarrativeTypes(state);
  const types = definitionSelectors.getEnabledDefinitions(state).types.byId;
  const eventTypes = eventTypesFromAllTypes(types);
  const nonSequenceParents = types.find(
    t => t.showInSequence && t.superTypes.find(st => !types.get(st).showInSequence)
  );

  const isNarrativeAllowed = definitionSelectors.isNarrativeAllowed(state);

  let isValid = true;
  let validationError = null;

  if (isNarrativeAllowed && narrativeTypes.size === 0) {
    isValid = false;
    validationError = settingsErrors.NO_NARRATIVE_TYPES;
  } else if (eventTypes.size === 0) {
    isValid = false;
    validationError = settingsErrors.NO_EVENT_TYPES;
  } else if (nonSequenceParents) {
    isValid = false;
    validationError = settingsErrors.EVENT_TYPE_PARENT;
  } else if (isNarrativeAllowed && narrativeSettingsSelectors.getNarrativeLabelCount(state) === 0) {
    isValid = false;
    validationError = settingsErrors.NO_NARRATIVE_LABEL;
  } else if (isNarrativeAllowed && narrativeSettingsSelectors.getNarrativeLabelAutoForFolderCount(state) === 0) {
    isValid = false;
    validationError = settingsErrors.NARRATIVE_LABEL_NO_AUTO_FOLDER;
  } else if (isNarrativeAllowed && narrativeSettingsSelectors.getNarrativeLabelAutoForOtherCount(state) === 0) {
    isValid = false;
    validationError = settingsErrors.NARRATIVE_LABEL_NO_AUTO_OTHER;
  }

  return {
    isValid: isValid,
    validationError: validationError
  };
};

const getZeroDateForCalendar = calendar => {
  const zeroTimestamp = getZeroDateTimestampFromCalendar(calendar);
  const precision = Precision.Day;
  return dateWithTimestamp(calendar, zeroTimestamp, precision);
};

const getEarliestItemZeroDate = state => {
  const calendar = calendarSelectors.getCalendar(state);
  const earliestEvent = itemSelectors.getEarliestItemOfAll(state);
  if (earliestEvent && earliestEvent.startDate && earliestEvent.startDate.timestamp) {
    return dateFunctions.dateAtStartOfWeekFromDate(calendar, earliestEvent.startDate);
  }
  return null;
};

const doesTimelineHaveItemsOrMarkers = state =>
  itemSelectors.getItemsById(state).size > 0 || calendarMarkerSelectors.getCalendarMarkersById(state).size > 0;

const getTypes = createSelector(
  state => state.definitions.types,
  colorSelectors.getColorsById,
  colorSelectors.getNearestColorMap,
  (types, colorsById, nearestColorMap) =>
    types.update("byId", typesById =>
      typesById.map(t =>
        t.id && !t.defaultItemColor
          ? t.set(
            "defaultItemColor",
            colorSelectors.getNearestColorNameByColorName(t.color, colorsById, nearestColorMap)
          )
          : t
      )
    )
);

export default {
  isSettingsValid,
  getAllOrderedTypes,
  getAllOrderedProperties,
  getAllOrderedReferences,
  getTypes,
  getAllOrderedNarrativeTypes,
  getFirstNarrativeTypeFromAllTypes,
  getNarrativeFolderType,
  eventTypesFromAllTypes,
  personTypesFromAllTypes,
  locationTypesFromAllTypes,
  otherTypesFromAllTypes,
  eventTypesFromAllTypesExcludingNarrativeFolder,
  personTypesFromAllTypesExcludingNarrativeFolder,
  locationTypesFromAllTypesExcludingNarrativeFolder,
  otherTypesFromAllTypesExcludingNarrativeFolder,
  eventToPersonReferences,
  eventToEventReferences,
  personToPersonReferences,
  subjectTypeReferences,
  propertiesForItemTypes,
  commonPropertiesForItemTypes,
  getZeroDateForCalendar,
  getEarliestItemZeroDate,
  doesTimelineHaveItemsOrMarkers
};
